import { useSyncExternalStore } from 'react';
import dayjs from 'dayjs';
import { getHr, hrApi, type Result } from './hrStore';
import { empById, fmtD, fmtMoney, nextCode, toDay, validateLeave, workingDaysBetween, type Log } from './hrLogic';
import {
  DEFAULT_TIME_SETTINGS, evalAssign, fmtMonth, isLocked, leaveOn, lockCheck, monthOf, otHours, reqDate, shiftAt, shiftById, shiftHours,
  validateAssign, validateShift, weekDays, type Assign, type AssignDraft, type PunchSrc, type ShiftDef, type TimeReq, type TimeSettings, type TpState,
} from './tpLogic';
import { ADJ_KIND, DEFAULT_PAY_SETTINGS, calcPeriodRows, fmtPayMonth, isPeriodLocked, periodBlockers, type Adjust, type PaySettings } from './payLogic';
import { TP_DEMO_VERSION, tpDemoState } from './tpDemo';

/**
 * Phân ca – chấm công – kỳ lương lưu ở localStorage (front-end trước, sau thay từng hàm tpApi bằng API).
 * Danh sách nhân sự, HĐ, nghỉ phép luôn đọc từ hrStore — đơn nghỉ được duyệt ghi thẳng vào hồ sơ nhân sự.
 */

const KEY = 'khothi.tp.v1';

function load(): TpState {
  const hr = getHr();
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const t = JSON.parse(raw) as TpState;
      const demo = t?.hrSeededAt != null;
      const stale = demo && ((t.demoVersion || 0) < TP_DEMO_VERSION || t.hrSeededAt !== hr.seededAt);
      if (t?.v === 1 && Array.isArray(t.assigns) && !stale) {
        return { ...t, time: { ...DEFAULT_TIME_SETTINGS, ...t.time }, pay: { ...DEFAULT_PAY_SETTINGS, ...t.pay } };
      }
    }
  } catch { /* dữ liệu hỏng → dựng lại demo */ }
  const t = tpDemoState(hr);
  try { localStorage.setItem(KEY, JSON.stringify(t)); } catch { /* bỏ qua */ }
  return t;
}

let state: TpState = load();
const listeners = new Set<() => void>();
const emit = () => listeners.forEach(l => l());

function set(fn: (t: TpState) => TpState): boolean {
  const next = fn(state);
  if (next === state) return true;
  try { localStorage.setItem(KEY, JSON.stringify(next)); } catch { return false; }
  state = next;
  emit();
  return true;
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', e => {
    if (e.key === KEY && e.newValue) { state = load(); emit(); }
  });
}

const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };
export const useTp = () => useSyncExternalStore(subscribe, () => state);
export const getTp = () => state;

const uid = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
const today = () => toDay();
const FULL = ['Bộ nhớ trình duyệt đầy, không lưu được'];
const fail = (...errors: string[]): Result => ({ ok: false, errors });
const save = (fn: (t: TpState) => TpState, extra: Partial<Result> = {}): Result => (set(fn) ? { ok: true, ...extra } : fail(...FULL));
const logged = (t: TpState, by: string, text: string): TpState => ({ ...t, log: [...t.log.slice(-300), { at: Date.now(), by, text } as Log] });
/** Dữ liệu công / điều chỉnh của tháng đổi → kỳ lương nháp cần tính lại */
const touch = (t: TpState, month: string): TpState => (t.periods.some(p => p.month === month && p.status === 'draft' && !p.stale)
  ? { ...t, periods: t.periods.map(p => (p.month === month && p.status === 'draft' ? { ...p, stale: true } : p)) } : t);
const empBranch = (empId: string) => empById(getHr(), empId)?.branchId || '';
const lockedFor = (empId: string, date: string) => isLocked(state, monthOf(date), empBranch(empId));
const stamp = (date: string, hhmm: string, after?: number) => {
  let t = dayjs(`${date}T${hhmm}`).valueOf();
  if (after && t < after) t = dayjs(t).add(1, 'day').valueOf();
  return t;
};

export type ReqDraft = Omit<TimeReq, 'id' | 'code' | 'status' | 'by' | 'at' | 'decidedBy' | 'decidedAt' | 'decideNote' | 'leaveId'>;

/** Kiểm tra đơn trước khi gửi */
export function validateRequest(d: ReqDraft, now = Date.now()): { errors: string[]; warnings: string[] } {
  const hr = getHr();
  const t = state;
  const errors: string[] = [];
  const warnings: string[] = [];
  const e = empById(hr, d.empId);
  if (!e) return { errors: ['Chọn nhân sự'], warnings };
  if (!d.reason?.trim()) errors.push('Nhập lý do');
  const date = d.kind === 'leave' ? d.from : d.date;
  if (!date) errors.push('Chọn ngày');
  else if (lockedFor(e.id, date)) errors.push(`Tháng ${fmtMonth(monthOf(date))} đã chốt công`);
  const td = dayjs(now).format('YYYY-MM-DD');
  if (d.kind === 'leave' && d.from) {
    const v = validateLeave(hr, e, { kind: d.leaveKind || 'annual', from: d.from, to: d.to || d.from, days: d.days || 0, reason: d.reason }, td);
    errors.push(...v.errors); warnings.push(...v.warnings);
    const dup = t.requests.find(r => r.empId === e.id && r.kind === 'leave' && r.status === 'pending' && r.from! <= (d.to || d.from!) && d.from! <= r.to!);
    if (dup) errors.push(`Đã có đơn ${dup.code} chờ duyệt cho khoảng ngày này`);
    const shifts = t.assigns.filter(a => a.empId === e.id && a.date >= d.from! && a.date <= (d.to || d.from!)).length;
    if (shifts) warnings.push(`${shifts} ca đã xếp trong thời gian nghỉ — người quản lý cần xếp người thay`);
  }
  if (d.kind === 'explain' && d.date) {
    const a = t.assigns.find(x => x.id === d.assignId);
    if (!a || a.empId !== e.id) errors.push('Chọn ca cần giải trình');
    if (!d.inTime && !d.outTime) errors.push('Nhập giờ vào hoặc giờ ra thực tế');
    if (d.date > td) errors.push('Chưa tới ngày làm việc');
    if (a && d.inTime && d.outTime) {
      const sh = shiftById(t, a.shiftId)!;
      const { startAt, endAt } = shiftAt(a.date, sh);
      const i = stamp(a.date, d.inTime);
      const o = stamp(a.date, d.outTime, i);
      if (o <= i) errors.push('Giờ ra phải sau giờ vào');
      if (i > endAt || o < startAt) errors.push('Giờ vào/ra không nằm trong ca');
    }
  }
  if (d.kind === 'ot' && d.date) {
    if (!d.otFrom || !d.otTo) errors.push('Nhập giờ bắt đầu và kết thúc làm thêm');
    else {
      const { hours } = otHours(d.otFrom, d.otTo);
      if (hours <= 0) errors.push('Khoảng giờ làm thêm không hợp lệ');
      const dayH = t.assigns.filter(a => a.empId === e.id && a.date === d.date).reduce((n, a) => n + shiftHours(shiftById(t, a.shiftId)!), 0);
      if (dayH + hours > 12) errors.push(`Tổng ${dayH + hours} giờ trong ngày — quá 12 giờ (ca ${dayH} + làm thêm ${hours})`);
      const sameMonth = t.requests.filter(r => r.empId === e.id && r.kind === 'ot' && (r.status === 'approved' || r.status === 'pending') && r.date?.startsWith(monthOf(d.date!)));
      const month = sameMonth.reduce((n, r) => n + (r.hours || 0), 0);
      if (month + hours > t.time.otMonthCap) errors.push(`Vượt trần ${t.time.otMonthCap} giờ làm thêm / tháng (đã có ${month} giờ)`);
      const year = t.requests.filter(r => r.empId === e.id && r.kind === 'ot' && r.status === 'approved' && r.date?.startsWith(d.date!.slice(0, 4))).reduce((n, r) => n + (r.hours || 0), 0);
      if (year + hours > t.time.otYearCap) warnings.push(`Vượt ${t.time.otYearCap} giờ làm thêm / năm (đã có ${year} giờ)`);
      if (!dayH) warnings.push('Ngày này không có ca — làm thêm vào ngày nghỉ');
    }
  }
  if (d.kind === 'swap') {
    const a = t.assigns.find(x => x.id === d.assignId);
    if (!a || a.empId !== e.id) errors.push('Chọn ca muốn đổi');
    else if (!d.toEmpId) errors.push('Chọn người nhận ca');
    else {
      if (shiftAt(a.date, shiftById(t, a.shiftId)!).startAt <= now) errors.push('Ca đã bắt đầu, không đổi được');
      const v = validateAssign(hr, t, { empId: d.toEmpId, date: a.date, shiftId: a.shiftId, branchId: a.branchId }, td);
      errors.push(...v.errors.map(x => `Người nhận: ${x}`)); warnings.push(...v.warnings.filter(x => !x.startsWith('Làm tăng cường')).map(x => `Người nhận: ${x}`));
    }
  }
  return { errors, warnings };
}

export const tpApi = {
  /* ============================== Ca làm việc ============================== */

  saveShift(d: Omit<ShiftDef, 'id'> & { id?: string }, by: string): Result {
    const errors = validateShift(state.shifts, d);
    if (errors.length) return { ok: false, errors };
    const used = d.id && state.assigns.some(a => a.shiftId === d.id && isLocked(state, monthOf(a.date), empBranch(a.empId)));
    const cur = d.id ? shiftById(state, d.id) : undefined;
    if (used && cur && (cur.start !== d.start || cur.end !== d.end || cur.work !== d.work || cur.breakMin !== d.breakMin)) {
      return fail('Ca đã dùng trong tháng đã chốt công — không sửa giờ / công; tạo ca mới thay thế');
    }
    const id = d.id || `sh-${uid()}`;
    return save(t => logged({ ...t, shifts: d.id ? t.shifts.map(x => (x.id === d.id ? { ...d, id } : x)) : [...t.shifts, { ...d, id }] }, by, `${d.id ? 'Sửa' : 'Thêm'} ca ${d.code}`), { id });
  },

  /* ============================== Xếp ca ============================== */

  assign(d: AssignDraft, by: string): Result {
    const v = validateAssign(getHr(), state, d, today());
    if (v.errors.length) return { ok: false, errors: v.errors, warnings: v.warnings };
    const id = `a-${uid()}`;
    const a: Assign = { id, empId: d.empId, date: d.date, shiftId: d.shiftId, branchId: d.branchId, by, at: Date.now() };
    return save(t => touch({ ...t, assigns: [...t.assigns, a] }, monthOf(d.date)), { id, warnings: v.warnings });
  },

  unassign(id: string, by: string): Result {
    const a = state.assigns.find(x => x.id === id);
    if (!a) return fail('Không tìm thấy ca');
    if (lockedFor(a.empId, a.date)) return fail('Tháng đã chốt công');
    if (a.inAt || a.outAt) return fail('Ca đã có giờ chấm công — không xóa được');
    return save(t => touch(logged({ ...t, assigns: t.assigns.filter(x => x.id !== id) }, by, `Bỏ ca ${shiftById(t, a.shiftId)?.code} ${fmtD(a.date)} của ${empById(getHr(), a.empId)?.name}`), monthOf(a.date)));
  },

  /** Chép lịch tuần trước sang tuần này (bỏ qua những ô không hợp lệ) */
  copyWeek(branchId: string, fromWeek: string, toWeek: string, by: string): Result {
    const from = weekDays(fromWeek);
    const to = weekDays(toWeek);
    const hr = getHr();
    let t = state;
    let added = 0;
    const skipped: string[] = [];
    state.assigns.filter(a => a.branchId === branchId && from.includes(a.date)).forEach(a => {
      const date = to[from.indexOf(a.date)];
      if (t.assigns.some(x => x.empId === a.empId && x.date === date && x.shiftId === a.shiftId)) return;
      const v = validateAssign(hr, t, { empId: a.empId, date, shiftId: a.shiftId, branchId }, today());
      if (v.errors.length) { skipped.push(`${empById(hr, a.empId)?.name} ${fmtD(date)}: ${v.errors[0]}`); return; }
      t = { ...t, assigns: [...t.assigns, { id: `a-${uid()}`, empId: a.empId, date, shiftId: a.shiftId, branchId, by, at: Date.now() }] };
      added += 1;
    });
    if (!added) return { ok: false, errors: [skipped.length ? `Không chép được ca nào. ${skipped[0]}` : 'Tuần trước chưa có ca nào'] };
    const r = save(() => touch(logged(t, by, `Chép ${added} ca sang tuần ${fmtD(toWeek)}`), monthOf(toWeek)), { id: String(added) });
    return { ...r, warnings: skipped.length ? [`Bỏ qua ${skipped.length} ca: ${skipped.slice(0, 3).join('; ')}${skipped.length > 3 ? '…' : ''}`] : undefined };
  },

  publish(branchId: string, week: string, by: string): Result {
    return save(t => logged({ ...t, published: [...t.published.filter(p => !(p.branchId === branchId && p.week === week)), { branchId, week, at: Date.now(), by }] }, by, `Công bố lịch tuần ${fmtD(week)}`));
  },

  /** Nhập giờ chấm từ máy / file: mỗi dòng (mã nhân sự hoặc mã chấm công, thời điểm) ghép vào ca gần nhất */
  importPunches(rows: { code: string; at: number }[], src: PunchSrc, by: string): Result {
    const hr = getHr();
    const byCode = new Map<string, string>();
    hr.employees.forEach(e => { byCode.set(e.code.toLowerCase(), e.id); if (e.timeCode) byCode.set(e.timeCode.toLowerCase(), e.id); });
    let t = state;
    let used = 0;
    const miss = new Set<string>();
    rows.forEach(r => {
      const empId = byCode.get(String(r.code).trim().toLowerCase());
      if (!empId) { miss.add(String(r.code)); return; }
      const cand = t.assigns.filter(a => a.empId === empId && Math.abs(dayjs(a.date).diff(dayjs(r.at).startOf('day'), 'day')) <= 1);
      const hit = cand.map(a => ({ a, w: shiftAt(a.date, shiftById(t, a.shiftId)!) }))
        .filter(({ w }) => r.at >= w.startAt - 3 * 3600000 && r.at <= w.endAt + 4 * 3600000)
        .sort((x, y) => Math.abs(x.w.startAt - r.at) - Math.abs(y.w.startAt - r.at))[0];
      if (!hit || isLocked(t, monthOf(hit.a.date), empBranch(empId))) return;
      const mid = (hit.w.startAt + hit.w.endAt) / 2;
      const a = hit.a;
      const patch: Partial<Assign> = r.at < mid
        ? { inAt: a.inAt ? Math.min(a.inAt, r.at) : r.at }
        : { outAt: a.outAt ? Math.max(a.outAt, r.at) : r.at };
      t = touch({ ...t, assigns: t.assigns.map(x => (x.id === a.id ? { ...x, ...patch, src } : x)) }, monthOf(a.date));
      used += 1;
    });
    if (!used) return { ok: false, errors: ['Không ghép được dòng nào vào ca đã xếp'], warnings: miss.size ? [`Không tìm thấy mã: ${Array.from(miss).slice(0, 5).join(', ')}`] : undefined };
    const r = save(() => logged(t, by, `Nhập ${used} lượt chấm công`), { id: String(used) });
    return { ...r, warnings: miss.size ? [`Không tìm thấy mã: ${Array.from(miss).slice(0, 5).join(', ')}`] : undefined };
  },

  /* ============================== Đơn từ ============================== */

  createRequest(d: ReqDraft, by: string): Result {
    const v = validateRequest(d);
    if (v.errors.length) return { ok: false, errors: v.errors, warnings: v.warnings };
    const id = `rq-${uid()}`;
    const date = reqDate(d as TimeReq);
    const r: TimeReq = {
      ...d, id, code: nextCode(state.requests.map(x => x.code), 'DT', date), status: 'pending', by, at: Date.now(), reason: d.reason.trim(),
      ...(d.kind === 'ot' && d.otFrom && d.otTo ? { hours: otHours(d.otFrom, d.otTo).hours, nightHours: otHours(d.otFrom, d.otTo).night } : {}),
      ...(d.kind === 'leave' ? { to: d.to || d.from } : {}),
    };
    return save(t => touch({ ...t, requests: [...t.requests, r] }, monthOf(date)), { id, warnings: v.warnings });
  },

  decide(id: string, approve: boolean, by: string, note?: string): Result {
    const r = state.requests.find(x => x.id === id);
    if (!r || r.status !== 'pending') return fail('Đơn không ở trạng thái chờ duyệt');
    if (lockedFor(r.empId, reqDate(r))) return fail('Tháng đã chốt công');
    if (!approve && !note?.trim()) return fail('Nhập lý do từ chối');
    const done = { decidedBy: by, decidedAt: Date.now(), decideNote: note?.trim() || undefined };
    if (!approve) return save(t => touch({ ...t, requests: t.requests.map(x => (x.id === id ? { ...x, status: 'rejected', ...done } : x)) }, monthOf(reqDate(r))));
    const v = validateRequest(r);
    const hard = v.errors.filter(x => !x.startsWith('Đã có đơn'));
    if (hard.length) return { ok: false, errors: hard };
    let patchAssign: ((a: Assign) => Assign) | null = null;
    let leaveId: string | undefined;
    let freed: string[] = [];
    if (r.kind === 'leave') {
      const res = hrApi.addLeave(r.empId, { kind: r.leaveKind || 'annual', from: r.from!, to: r.to!, days: r.days!, reason: `${r.code}: ${r.reason}` }, by);
      if (!res.ok) return res;
      leaveId = res.id;
      // Nghỉ trọn ngày: bỏ các ca chưa chấm công trong thời gian nghỉ
      if (r.days! >= workingDaysBetween(r.from!, r.to!)) freed = state.assigns.filter(a => a.empId === r.empId && a.date >= r.from! && a.date <= r.to! && !a.inAt && !a.outAt).map(a => a.id);
    }
    if (r.kind === 'explain') {
      const a = state.assigns.find(x => x.id === r.assignId)!;
      const inAt = r.inTime ? stamp(a.date, r.inTime) : a.inAt;
      const outAt = r.outTime ? stamp(a.date, r.outTime, inAt) : a.outAt;
      patchAssign = x => ({ ...x, inAt, outAt, src: 'manual', note: `${r.code}: ${r.reason}` });
    }
    if (r.kind === 'swap') patchAssign = x => ({ ...x, empId: r.toEmpId!, note: `Đổi ca theo ${r.code}`, by, at: Date.now() });
    return save(t => touch(logged({
      ...t,
      requests: t.requests.map(x => (x.id === id ? { ...x, status: 'approved', leaveId, ...done } : x)),
      assigns: (patchAssign ? t.assigns.map(a => (a.id === r.assignId ? patchAssign!(a) : a)) : t.assigns).filter(a => !freed.includes(a.id)),
    }, by, `Duyệt ${r.code}`), monthOf(reqDate(r))), { warnings: freed.length ? [`Đã bỏ ${freed.length} ca trong thời gian nghỉ — cần xếp người thay`] : undefined });
  },

  /** Hủy đơn chờ duyệt; đơn nghỉ / làm thêm đã duyệt hủy được khi chưa chốt công */
  cancelRequest(id: string, by: string): Result {
    const r = state.requests.find(x => x.id === id);
    if (!r) return fail('Không tìm thấy đơn');
    if (r.status === 'approved') {
      if (r.kind !== 'leave' && r.kind !== 'ot') return fail('Đơn đã duyệt và đã cập nhật chấm công — không hủy được');
      if (lockedFor(r.empId, reqDate(r))) return fail('Tháng đã chốt công');
      if (r.leaveId) hrApi.removeLeave(r.empId, r.leaveId, by);
    } else if (r.status !== 'pending') return fail('Đơn đã kết thúc');
    return save(t => touch(logged({ ...t, requests: t.requests.map(x => (x.id === id ? { ...x, status: 'cancelled', decidedBy: by, decidedAt: Date.now() } : x)) }, by, `Hủy ${r.code}`), monthOf(reqDate(r))));
  },

  /* ============================== Chốt công ============================== */

  lockMonth(month: string, branchId: string, by: string): Result {
    const hr = getHr();
    const v = lockCheck(hr, state, month, branchId, today());
    if (v.errors.length) return { ok: false, errors: v.errors, warnings: v.warnings };
    const r = save(t => logged({ ...t, locks: [...t.locks, { month, branchId, at: Date.now(), by }] }, by, `Chốt công ${fmtMonth(month)} · ${hr.branches.find(b => b.id === branchId)?.short}`));
    // Thủ tục nghỉ việc: đánh dấu "Chốt công" cho người nghỉ trong tháng
    hr.offboardings.filter(o => o.status === 'open' && !o.tasks.timesheet && o.lastDay.startsWith(month) && empById(hr, o.empId)?.branchId === branchId)
      .forEach(o => hrApi.toggleOffTask(o.id, 'timesheet', true, by));
    return { ...r, warnings: v.warnings };
  },

  unlockMonth(month: string, branchId: string, by: string): Result {
    if (!isLocked(state, month, branchId)) return fail('Tháng chưa chốt');
    if (isPeriodLocked(state, month)) return fail(`${fmtPayMonth(month)} đã gửi duyệt lương — trả bảng lương về nháp trước khi mở chốt`);
    return save(t => touch(logged({ ...t, locks: t.locks.filter(l => !(l.month === month && l.branchId === branchId)) }, by, `Mở chốt công ${fmtMonth(month)}`), month));
  },

  /* ============================== Thiết lập ============================== */

  saveTimeSettings(patch: Partial<TimeSettings>, by: string): Result {
    if (patch.graceMin != null && (patch.graceMin < 0 || patch.graceMin > 30)) return fail('Số phút cho phép đi muộn từ 0 tới 30');
    if (patch.weeklyOff && !patch.weeklyOff.length) return fail('Chọn ít nhất 1 ngày nghỉ hằng tuần');
    return save(t => logged({ ...t, time: { ...t.time, ...patch } }, by, 'Cập nhật thiết lập chấm công'));
  },

  savePaySettings(patch: Partial<PaySettings>, by: string): Result {
    const p = { ...state.pay, ...patch };
    if (!(p.baseWage > 0) || !(p.regionMin > 0)) return fail('Nhập lương cơ sở và lương tối thiểu vùng');
    if (p.brackets.some((b, i) => i < p.brackets.length - 1 && (b.upTo == null || (i && b.upTo <= p.brackets[i - 1].upTo!)))) return fail('Biểu thuế: mức trên của các bậc phải tăng dần');
    if (p.allowances.some(a => !a.name.trim() || !(a.amount >= 0))) return fail('Phụ cấp: nhập tên và số tiền');
    return save(t => logged({ ...t, pay: p, periods: t.periods.map(x => (x.status === 'draft' ? { ...x, stale: true } : x)) }, by, 'Cập nhật thiết lập lương'));
  },

  /* ============================== Khoản điều chỉnh ============================== */

  saveAdjust(d: Omit<Adjust, 'id' | 'by' | 'at'> & { id?: string }, by: string): Result {
    if (!d.empId) return fail('Chọn nhân sự');
    if (!(d.amount > 0)) return fail('Số tiền phải lớn hơn 0');
    if (!d.note?.trim()) return fail('Nhập nội dung');
    if (isPeriodLocked(state, d.month)) return fail(`${fmtPayMonth(d.month)} đã gửi duyệt — đưa vào kỳ sau`);
    const old = d.id ? state.adjustments.find(x => x.id === d.id) : undefined;
    if (old && isPeriodLocked(state, old.month)) return fail('Kỳ lương của khoản này đã gửi duyệt');
    const warnings: string[] = [];
    const e = empById(getHr(), d.empId);
    if (d.kind === 'advance' && e) {
      const total = state.adjustments.filter(x => x.empId === d.empId && x.month === d.month && x.kind === 'advance' && x.id !== d.id).reduce((n, x) => n + x.amount, 0) + d.amount;
      const sal = getHr().contracts.filter(c => c.empId === d.empId && !c.cancelled).sort((a, b) => a.start.localeCompare(b.start)).pop()?.salary || 0;
      if (sal && total > sal * 0.5) warnings.push(`Tạm ứng ${fmtMoney(total)} vượt 50% lương tháng`);
    }
    if (e?.leftDate && e.leftDate < `${d.month}-01`) return fail(`${e.name} đã nghỉ việc từ ${fmtD(e.leftDate)}`);
    const id = d.id || `adj-${uid()}`;
    const row: Adjust = { ...d, id, note: d.note.trim(), by, at: Date.now() };
    return save(t => touch(touch(logged({ ...t, adjustments: d.id ? t.adjustments.map(x => (x.id === d.id ? row : x)) : [...t.adjustments, row] }, by, `${ADJ_KIND[d.kind].label} ${e?.name} ${fmtMoney(d.amount)}`), d.month), old?.month || d.month), { id, warnings });
  },

  removeAdjust(id: string, by: string): Result {
    const a = state.adjustments.find(x => x.id === id);
    if (!a) return fail('Không tìm thấy');
    if (isPeriodLocked(state, a.month)) return fail('Kỳ lương đã gửi duyệt');
    return save(t => touch(logged({ ...t, adjustments: t.adjustments.filter(x => x.id !== id) }, by, `Xóa ${ADJ_KIND[a.kind].label.toLowerCase()} ${fmtMoney(a.amount)}`), a.month));
  },

  /* ============================== Kỳ lương ============================== */

  /** Tạo hoặc tính lại kỳ lương nháp */
  calcPeriod(month: string, by: string): Result {
    const cur = state.periods.find(p => p.month === month);
    if (cur && cur.status !== 'draft') return fail('Kỳ lương đã gửi duyệt — trả về nháp để tính lại');
    const block = periodBlockers(getHr(), state, month);
    if (block.length) return { ok: false, errors: block };
    const rows = calcPeriodRows(getHr(), state, month);
    const id = cur?.id || `pp-${uid()}`;
    const now = Date.now();
    const p = { id, month, status: 'draft' as const, rows, calcAt: now, calcBy: by, stale: false, log: [...(cur?.log || []), { at: now, by, text: cur ? 'Tính lại' : 'Tạo kỳ lương' }] };
    return save(t => ({ ...t, periods: cur ? t.periods.map(x => (x.id === id ? p : x)) : [...t.periods, p] }), { id });
  },

  submitPeriod(id: string, by: string): Result {
    const p = state.periods.find(x => x.id === id);
    if (!p || p.status !== 'draft') return fail('Kỳ lương không ở trạng thái nháp');
    if (p.stale) return fail('Dữ liệu đã thay đổi sau lần tính gần nhất — bấm Tính lại trước khi gửi duyệt');
    const neg = p.rows.filter(r => r.net < 0);
    if (neg.length) return fail(`${neg.length} người có lương thực nhận âm: ${neg.slice(0, 3).map(r => r.name).join(', ')}`);
    return save(t => ({ ...t, periods: t.periods.map(x => (x.id === id ? { ...x, status: 'pending', submittedAt: Date.now(), submittedBy: by, rejectNote: undefined, log: [...x.log, { at: Date.now(), by, text: 'Gửi duyệt' }] } : x)) }));
  },

  approvePeriod(id: string, by: string): Result {
    const p = state.periods.find(x => x.id === id);
    if (!p || p.status !== 'pending') return fail('Kỳ lương không ở trạng thái chờ duyệt');
    if (p.submittedBy === by) return fail('Người lập bảng lương không tự duyệt');
    return save(t => ({ ...t, periods: t.periods.map(x => (x.id === id ? { ...x, status: 'approved', approvedAt: Date.now(), approvedBy: by, log: [...x.log, { at: Date.now(), by, text: 'Duyệt bảng lương' }] } : x)) }));
  },

  rejectPeriod(id: string, by: string, note: string): Result {
    const p = state.periods.find(x => x.id === id);
    if (!p || (p.status !== 'pending' && p.status !== 'approved')) return fail('Chỉ trả về bảng lương chờ duyệt / đã duyệt chưa chi');
    if (!note.trim()) return fail('Nhập lý do trả về');
    return save(t => ({ ...t, periods: t.periods.map(x => (x.id === id ? { ...x, status: 'draft', stale: true, rejectNote: note.trim(), approvedAt: undefined, approvedBy: undefined, log: [...x.log, { at: Date.now(), by, text: `Trả về: ${note.trim()}` }] } : x)) }));
  },

  markPaid(id: string, by: string): Result {
    const p = state.periods.find(x => x.id === id);
    if (!p || p.status !== 'approved') return fail('Bảng lương chưa được duyệt');
    const r = save(t => ({ ...t, periods: t.periods.map(x => (x.id === id ? { ...x, status: 'paid', paidAt: Date.now(), paidBy: by, log: [...x.log, { at: Date.now(), by, text: 'Đã chi lương' }] } : x)) }));
    // Thủ tục nghỉ việc: đánh dấu "Thanh toán lương" cho người nghỉ trong kỳ
    const hr = getHr();
    const ids = new Set(p.rows.map(x => x.empId));
    hr.offboardings.filter(o => o.status === 'open' && !o.tasks.settlement && ids.has(o.empId) && o.lastDay.startsWith(p.month))
      .forEach(o => hrApi.toggleOffTask(o.id, 'settlement', true, by));
    return r;
  },

  sendPayslips(id: string, empIds: string[] | null, by: string): Result {
    const p = state.periods.find(x => x.id === id);
    if (!p || (p.status !== 'approved' && p.status !== 'paid')) return fail('Chỉ gửi phiếu lương khi bảng lương đã duyệt');
    const now = Date.now();
    const n = p.rows.filter(r => !empIds || empIds.includes(r.empId)).length;
    return save(t => ({ ...t, periods: t.periods.map(x => (x.id === id ? { ...x, rows: x.rows.map(r => (!empIds || empIds.includes(r.empId) ? { ...r, sentAt: now } : r)), log: [...x.log, { at: now, by, text: `Gửi ${n} phiếu lương` }] } : x)) }), { id: String(n) });
  },

  loadDemo() { set(() => tpDemoState(getHr())); },
};

/* ----- Tiện ích cho màn hình ----- */

/** Trạng thái các ca hôm nay của một người */
export function todayEvals(empId: string, now = Date.now()) {
  const d = dayjs(now).format('YYYY-MM-DD');
  const e = empById(getHr(), empId);
  return state.assigns.filter(a => a.empId === empId && a.date === d).map(a => {
    const sh = shiftById(state, a.shiftId)!;
    return evalAssign(a, sh, { now, grace: state.time.graceMin, leave: !!e && (leaveOn(e, d)?.part || 0) >= 1 });
  });
}
